import { defineStore } from "pinia";
import { Http_getPaymentOptionList } from "@/http/apiFunction";
import {
  getStorageWithSession,
  setStorageWithSession,
  getCurrentSessionId,
} from "@/utils/storage";

const CACHE_KEY = "payment_option_list";

export interface PaymentOption {
  id: number;
  nameCn: string;
}

export interface PaymentMethodConfig {
  [key: number]: string;
}

export interface TState {
  paymentOptionList: PaymentOption[];
  paymentMethodConfig: PaymentMethodConfig;
  isLoading: boolean;
  lastUpdated: number | null;
}

export interface objInfo {
  [key: string]: any;
}

export const usePaymentStore = defineStore({
  id: "payment",
  state: (): TState => ({
    paymentOptionList: [],
    paymentMethodConfig: {},
    isLoading: false,
    lastUpdated: null,
  }),
  getters: {
    isCached: (state) => {
      // 检查是否有有效的缓存数据
      const cachedData = getStorageWithSession<{
        paymentOptionList: PaymentOption[];
        paymentMethodConfig: PaymentMethodConfig;
      }>(CACHE_KEY);
      return !!cachedData;
    },
  },
  actions: {
    updatePaymentOptionList(value: PaymentOption[]) {
      this.paymentOptionList = value;
    },

    updatePaymentMethodConfig(value: PaymentMethodConfig) {
      this.paymentMethodConfig = value;
    },

    // 获取支付方式列表，支持强制刷新
    async getPaymentOptions(forceRefresh = false): Promise<{
      paymentOptionList: PaymentOption[];
      paymentMethodConfig: PaymentMethodConfig;
    }> {
      if (this.isLoading) {
        // 如果正在加载，等待加载完成
        while (this.isLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return {
          paymentOptionList: this.paymentOptionList,
          paymentMethodConfig: this.paymentMethodConfig,
        };
      }

      // 如果已有数据且不是强制刷新，直接返回
      if (
        this.paymentOptionList.length > 0 &&
        !forceRefresh &&
        !this.isExpired()
      ) {
        return {
          paymentOptionList: this.paymentOptionList,
          paymentMethodConfig: this.paymentMethodConfig,
        };
      }

      this.isLoading = true;

      try {
        // 如果不强制刷新，先尝试从缓存获取
        if (!forceRefresh) {
          const cachedData = getStorageWithSession<{
            paymentOptionList: PaymentOption[];
            paymentMethodConfig: PaymentMethodConfig;
          }>(CACHE_KEY);

          if (cachedData) {
            this.updatePaymentOptionList(cachedData.paymentOptionList);
            this.updatePaymentMethodConfig(cachedData.paymentMethodConfig);
            this.lastUpdated = Date.now();
            return cachedData;
          }
        }

        // 无缓存则从接口获取数据
        const res = await Http_getPaymentOptionList();

        if (res.code === 0 && res.data) {
          const paymentMethodConfig: PaymentMethodConfig = { 0: "全部" };

          // 构建配置对象
          res.data.forEach((item: any) => {
            paymentMethodConfig[item.id] = item.modeName;
          });

          const paymentOptionList = this.mapToList(paymentMethodConfig);

          const result = {
            paymentOptionList,
            paymentMethodConfig,
          };

          // 更新 store 状态
          this.updatePaymentOptionList(result.paymentOptionList);
          this.updatePaymentMethodConfig(result.paymentMethodConfig);
          this.lastUpdated = Date.now();

          // 缓存数据
          setStorageWithSession(CACHE_KEY, result);

          return result;
        }

        // 接口返回失败，返回默认数据
        const defaultResult = {
          paymentOptionList: [],
          paymentMethodConfig: {},
        };

        this.updatePaymentOptionList(defaultResult.paymentOptionList);
        this.updatePaymentMethodConfig(defaultResult.paymentMethodConfig);

        return defaultResult;
      } catch (error) {
        console.error("获取支付方式列表失败:", error);

        // 发生错误，返回默认数据
        const defaultResult = {
          paymentOptionList: [],
          paymentMethodConfig: {},
        };

        this.updatePaymentOptionList(defaultResult.paymentOptionList);
        this.updatePaymentMethodConfig(defaultResult.paymentMethodConfig);

        return defaultResult;
      } finally {
        this.isLoading = false;
      }
    },

    // 将对象转换为列表格式
    mapToList(obj: objInfo): PaymentOption[] {
      return Object.keys(obj).map((key: string) => {
        return {
          id: Number(key),
          nameCn: obj[Number(key)],
        };
      });
    },

    // 检查数据是否过期（可选，比如24小时过期）
    isExpired(): boolean {
      if (!this.lastUpdated) return true;
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000; // 24小时
      return now - this.lastUpdated > oneDay;
    },

    // 清除缓存和状态
    async clearPaymentCache() {
      // 清除本地缓存
      localStorage.removeItem(CACHE_KEY);
      // 重置 store 状态
      this.paymentOptionList = [];
      this.paymentMethodConfig = {};
      this.lastUpdated = null;
    },

    // 初始化会话（在应用启动时调用）
    initializeSession() {
      getCurrentSessionId(); // 确保会话ID已初始化
    },
  },
});
